import type { TFunction } from 'i18next';

import { EGoalType, type Goal } from '@feathr/blackbox';
import { currencyFormatter } from '@feathr/extender/utils';
import type { ListResponse } from '@feathr/rachis';

/** Returns true when the goals collection has loaded and contains at least one goal. */
export function hasAnyGoals(goals: ListResponse<Goal>): boolean {
  return goals.isLoaded && goals.models.length > 0;
}

/** Donation goals display "Variable" rather than a fixed currency amount. */
export function formatGoalValue(goal: Goal, t: TFunction): string {
  if (goal.get('conv_type') === EGoalType.donation_form_submission) {
    return t('Variable');
  }
  return currencyFormatter.format(goal.get('conv_value'));
}

/**
 * Compares two sort values, with empty strings always sorting last regardless of direction.
 *
 * @param av - Sort value for item a.
 * @param bv - Sort value for item b.
 * @param desc - Whether to sort descending.
 * @returns Negative if a should come first, positive if b should come first, 0 if equal.
 */
export function compareGoalValues(av: string | number, bv: string | number, desc: boolean): number {
  if (av === '' && bv !== '') {
    return 1;
  }
  if (av !== '' && bv === '') {
    return -1;
  }
  const d = desc ? -1 : 1;
  if (typeof av === 'string' && typeof bv === 'string') {
    const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    return cmp !== 0 ? cmp * d : 0;
  }
  if (av > bv) {
    return d;
  }
  if (av < bv) {
    return -d;
  }
  return 0;
}

/** Returns a comparable sort value; Infinity for donation conv_value keeps "Variable" separate from $0 goals. */
export function getGoalSortValue(goal: Goal, id: string): string | number {
  if (id === 'conv_value') {
    return goal.get('conv_type') === EGoalType.donation_form_submission
      ? Infinity
      : goal.get('conv_value') ?? 0;
  }
  return goal.get(id) ?? '';
}
